import React from 'react';
import { Link } from 'react-router-dom';
import { Progress } from './ui/progress';
import { ClipboardList, ChevronRight, Circle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// Dashboard task card for pending coach questionnaires.
// Style matches the other DashboardPage task-card shell (TaskCard in DashboardPage.js):
// glass background, 12x12 icon tile, uppercase Barlow Condensed eyebrow title,
// orange accent. Revision-requested cards get a brighter border so they stand out
// without becoming the full "urgent" treatment reserved for overdue check-ins.
export default function QuestionnaireTaskCard({ data }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // data: { has_pending: bool, count: number, items: [{ id, title, status, answered, total, is_revision }] }
  if (!data?.has_pending) return null;

  const multi = data.count > 1;
  const first = data.items?.[0] || {};
  const href = multi ? '/questionnaires' : `/questionnaires/${first.id}`;
  const title = multi
    ? `${data.count} questionnaires from your coach`
    : (first.title || 'Questionnaire');
  const total = first.total || 0;
  const answered = first.answered || 0;
  const progress = !multi && total > 0 ? (answered / total) * 100 : 0;
  const subtitle = multi
    ? 'Tap to view'
    : (first.is_revision ? 'Coach requested changes' : `${answered}/${total} answered`);

  const accent = first.is_revision
    ? {
        border: '1px solid rgba(255,92,0,0.55)',
        boxShadow: isDark
          ? '0 4px 24px rgba(255,92,0,0.18), inset 0 1px 0 rgba(255,255,255,0.08)'
          : '0 4px 24px rgba(255,92,0,0.18), 0 1px 2px rgba(0,0,0,0.04)',
      }
    : {
        border: '1px solid rgba(255,92,0,0.28)',
        boxShadow: isDark
          ? '0 4px 24px rgba(255,92,0,0.10), inset 0 1px 0 rgba(255,255,255,0.08)'
          : '0 4px 24px rgba(255,92,0,0.10), 0 1px 2px rgba(0,0,0,0.04)',
      };

  const shellStyle = isDark
    ? {
        background: 'linear-gradient(135deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.03) 100%)',
        backdropFilter: 'blur(20px) saturate(150%)',
        WebkitBackdropFilter: 'blur(20px) saturate(150%)',
        ...accent,
      }
    : {
        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        ...accent,
      };

  return (
    <Link to={href} className="block">
      <div
        className="relative rounded-2xl p-4 transition-all duration-300"
        style={shellStyle}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className="relative w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-[#FF8C42]"
              style={{ background: isDark ? 'rgba(255,92,0,0.10)' : 'rgba(255,92,0,0.12)' }}
            >
              <ClipboardList className="h-5 w-5" />
            </div>
            <span
              className="text-xs font-bold tracking-[0.18em] text-zinc-500 dark:text-gray-400 uppercase truncate"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {multi ? 'Questionnaires' : 'Questionnaire'}
            </span>
          </div>
          <Circle className={`h-5 w-5 shrink-0 ${isDark ? 'text-white/20' : 'text-black/20'}`} />
        </div>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-zinc-900 dark:text-white truncate">{title}</p>
            <p className="text-xs text-zinc-600 dark:text-gray-400 truncate">{subtitle}</p>
            {!multi && total > 0 && (
              <Progress value={progress} className="h-1 mt-1.5" />
            )}
          </div>
          <ChevronRight className="h-5 w-5 text-zinc-500 dark:text-gray-400 shrink-0" />
        </div>
      </div>
    </Link>
  );
}
